import { EReportTaskStatus, type IReportCardInfo, type REPORT_TEMPLATE } from '@/types';
import { AbstractModelImpl } from '../common/abstract-model';
import type { ReportApiService } from '@/infrastructure/api-functions/report-api-service';
import type { IReferenceModelService } from './reference-model-service';
import type { ISolution } from '@eureka/shared/solution-card/src/types';

export interface BaseReportCardData<T> {
    isLoading: boolean;
    isFailed: boolean;
    isSuccess: boolean;
    data?: T;
}

export interface BaseCardService<F> extends AbstractModelImpl<BaseReportCardData<F>> {
    startFetch(projectId: string, groupKey: string, isRetry?: boolean): Promise<void> | undefined;
    updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void;
    templateType: REPORT_TEMPLATE;
    setCurrentTaskGroupType(taskGroupType: string): void;
}

export abstract class BaseCardServiceImpl<T> extends AbstractModelImpl<BaseReportCardData<T>> implements BaseCardService<T> {
    abstract updateFetchData(getUpdateValue: (v: BaseReportCardData<IReportCardInfo>) => BaseReportCardData<IReportCardInfo>): void

    abstract templateType: REPORT_TEMPLATE;

    private _timer: number | null = null;

    private _isFetching = false

    protected _reportApiService: ReportApiService;
    protected _referenceService?: IReferenceModelService;
    protected currentTaskGroupType?: string;

    constructor(reportApiService: ReportApiService, referenceService?: IReferenceModelService) {
        super();
        this._reportApiService = reportApiService;
        this._referenceService = referenceService;
    }

    /**
     * 设置当前任务组类型（供内部自动注册使用）
     */
    setCurrentTaskGroupType(taskGroupType: string): void {
        this.currentTaskGroupType = taskGroupType;
    }

    /**
     * 自动注册引用数据
     * @param ragList - 引用数据列表
     */
    protected _autoRegisterReferences(ragList: ISolution[]): void {
        if (!this.currentTaskGroupType || !ragList || ragList.length === 0 || !this._referenceService) return;

        this._referenceService.registerModuleReferences(
            this.templateType,
            this.currentTaskGroupType,
            ragList
        );
    }

    startFetch(projectId: string, groupKey: string, isRetry?: boolean) {
        if (this._isFetching) {
            return;
        }
        if (isRetry) {
            // 重试清空状态
            this.updateFetchData((v) => { return { ...v, isFailed: false, data: undefined } });
        }

        // 设置当前任务组类型
        this.setCurrentTaskGroupType(groupKey);

        this._isFetching = true;

        this.updateFetchData((v) => { return { ...v, isLoading: true } });

        return this._reportApiService.getReportCardData({ projectId, taskGroupKey: groupKey, isRetry })
            .then(detail => {
                this._isFetching = false;
                if (detail) {
                    if (detail.task_status === EReportTaskStatus.PROCESSING) {
                        this._timer && clearTimeout(this._timer);
                        this._isFetching = true;
                        setTimeout(() => this.startFetch(projectId, groupKey), 2000);
                    } else {
                        const isFailed = detail.task_status === EReportTaskStatus.FAILED;
                        const isSuccess = detail.task_status === EReportTaskStatus.FINISH;
                        this.updateFetchData((v) => { return { ...v, data: detail, isFailed, isSuccess, isLoading: false } });
                    }
                }
            })
            .catch(e => {
                this.updateFetchData((v) => { return { ...v, isLoading: false, isFailed: true } });
                throw e;
            })
            .finally(() => {
                this._isFetching = false;
            });
    }
}
